import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, X } from "lucide-react";
import { Database } from "@/integrations/supabase/types";
import { useTranslation } from "react-i18next";

type EntryType = Database['public']['Enums']['changelog_entry_type'];

interface FilterControlsProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  selectedTypes: EntryType[];
  onTypeToggle: (type: EntryType) => void;
  onClearFilters: () => void;
}

const FilterControls = ({
  searchTerm,
  onSearchChange,
  selectedTypes,
  onTypeToggle,
  onClearFilters
}: FilterControlsProps) => {
  const { t } = useTranslation('changelog');

  const entryTypes: { type: EntryType; label: string; color: string }[] = [
    { type: 'feature', label: t('filters.types.feature'), color: 'bg-green-100 text-green-800 border-green-200' },
    { type: 'improvement', label: t('filters.types.improvement'), color: 'bg-blue-100 text-blue-800 border-blue-200' },
    { type: 'bugfix', label: t('filters.types.bugfix'), color: 'bg-red-100 text-red-800 border-red-200' },
    { type: 'security', label: t('filters.types.security'), color: 'bg-orange-100 text-orange-800 border-orange-200' },
    { type: 'breaking', label: t('filters.types.breaking'), color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    { type: 'deprecated', label: t('filters.types.deprecated'), color: 'bg-gray-100 text-gray-800 border-gray-200' },
    { type: 'removed', label: t('filters.types.removed'), color: 'bg-red-100 text-red-800 border-red-200' }
  ];

  const hasActiveFilters = searchTerm || selectedTypes.length > 0;

  return (
    <div className="bg-card border rounded-lg p-4 mb-6">
      <div className="flex flex-col gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('filters.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex flex-wrap gap-2">
          <span className="text-sm font-medium text-foreground self-center">{t('filters.filterLabel')}</span>
          {entryTypes.map(({ type, label, color }) => (
            <Badge
              key={type}
              variant={selectedTypes.includes(type) ? "default" : "outline"}
              className={`cursor-pointer transition-colors ${
                selectedTypes.includes(type) ? color : 'hover:bg-muted'
              }`}
              onClick={() => onTypeToggle(type)}
            >
              {label}
            </Badge>
          ))}
          
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="h-6 px-2 text-xs"
            >
              <X className="w-3 h-3 mr-1" />
              {t('filters.clearFilters')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterControls;